import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import CopyPill from '../../reusable/InfoPanel/CopyPill';
import InfoPanel from '../../reusable/InfoPanel/InfoPanel';
import InfoField from '../../reusable/InfoPanel/InfoField';
import InfoRow from '../../reusable/InfoPanel/InfoRow';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import { selectAccount, selectPickedAccount } from '../../store/slices/accountSlice';
import { useGetAccountSetupQuery } from '../../store/api/accountSetupApi';
import { useGetAccountsQuery } from '../../store/api/dashboardApi';
import { formatLoginId } from '../../utilities/accounts';
import { NA, usd } from '../../utilities/format';
import { t } from '../../i18n/i18n';
import { buildFirstTradeSteps, PLATFORMS } from './data';
import './AccountSetup.scss';

/**
 * A secret that reveals in place rather than sending you elsewhere to read it.
 * Provides both Show/Hide toggling and a direct Copy action.
 * With none on record the row offers to set one instead.
 */
function SecretField({ label, secret }) {
  const [shown, setShown] = useState(false);

  return (
    <InfoField
      label={label}
      value={
        <span
          className="ip-field__secret"
          title={shown && secret ? secret : undefined}
        >
          {shown && secret ? secret : '••••••••'}
        </span>
      }
      action={
        secret ? (
          <div className="as-secret-actions">
            <button
              type="button"
              className="ip-mini"
              aria-pressed={shown}
              onClick={() => setShown((was) => !was)}
            >
              {shown ? t('Hide') : t('Show')}
            </button>
            <CopyPill value={secret} label={label} />
          </div>
        ) : (
          <span className="ip-mini is-inert">{t('Set')}</span>
        )
      }
    />
  );
}

/** Title-case a string ("alpha pro" -> "Alpha Pro") */
const titleCase = (str) =>
  typeof str === 'string'
    ? str
        .toLowerCase()
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : str;

/** Format account size to "5K Challenge", "50K Challenge", "100K Challenge" */
const formatAccountSizeLabel = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1e6) return t('{{size}} Challenge', { size: `${(n / 1e6).toFixed(n % 1e6 ? 1 : 0)}M` });
  if (n >= 1e3) return t('{{size}} Challenge', { size: `${(n / 1e3).toLocaleString('en-US')}K` });
  return t('{{size}} Challenge', { size: n.toLocaleString('en-US') });
};

/** Format purchase date to "10 Mar 2025" */
const formatPurchaseDate = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? format(parsed, 'dd MMM yyyy') : value;
};

/**
 * Format receipt amount.
 * The payment backend sends minor units (cents, e.g. "7700" = $77.00) unless
 * already decimal-delimited or dollar-prefixed.
 */
const formatReceiptAmount = (value) => {
  if (value == null || value === '') return NA;
  if (typeof value === 'string' && value.trim().startsWith('$')) return value.trim();
  const str = String(value).trim();
  const num = Number(str);
  if (!Number.isFinite(num)) return str;
  const dollars = str.includes('.') ? num : num / 100;
  return usd(dollars, { decimals: 2 });
};

export default function AccountSetup() {
  const [params, setParams] = useSearchParams();
  const dispatch = useDispatch();
  const picked = useSelector(selectPickedAccount);

  const { data: accountList, isLoading: isLoadingAccounts } = useGetAccountsQuery({
    page: 1,
    page_size: 100,
  });
  const accounts = accountList?.accounts?.results ?? [];

  const rawLogin = params.get('login_id') ?? params.get('login');
  const candidateLogin = rawLogin ?? picked.loginId;
  const matchedAccount = accounts.find(
    (a) => String(a.login_id) === String(candidateLogin)
  );
  // A login missing from the list — one bought since the list was cached, or
  // past its first hundred — is looked up by itself before it is used, as on
  // Account Metrics. One that isn't the trader's is never sent: the address can
  // name someone else's account, and asking for it was a 401.
  const lookup = useGetAccountsQuery(
    { page: 1, page_size: 100, search: String(candidateLogin) },
    { skip: !candidateLogin || isLoadingAccounts || Boolean(matchedAccount) },
  );
  const lookedUp = lookup.data?.accounts?.results?.find(
    (a) => String(a.login_id) === String(candidateLogin),
  );
  const isResolving =
    Boolean(candidateLogin) &&
    (isLoadingAccounts || (!matchedAccount && !lookup.isSuccess && !lookup.isError));
  const activeAccount = matchedAccount ?? lookedUp ?? (isResolving ? null : accounts[0]);

  const loginId = activeAccount ? String(activeAccount.login_id) : null;
  const platform =
    (String(activeAccount?.login_id) === String(rawLogin) ? params.get('platform') : null) ??
    activeAccount?.platform ??
    picked.platform ??
    null;

  /* Sync Redux picker to the URL if they differ */
  useEffect(() => {
    if (
      loginId &&
      (String(picked.loginId) !== String(loginId) ||
        (platform && picked.platform !== platform))
    ) {
      dispatch(selectAccount({ loginId, platform }));
    }
  }, [loginId, platform, picked.loginId, picked.platform, dispatch]);

  /* Puts the account in the address bar so it names what you are looking at and
     survives a copy or reload. */
  useEffect(() => {
    if (loginId && params.get('login') !== loginId && params.get('login_id') !== loginId) {
      const next = new URLSearchParams(params);
      // A `login_id` that fell back (not the trader's) would go on naming it.
      next.delete('login_id');
      next.set('login', loginId);
      if (platform) next.set('platform', platform);
      setParams(next, { replace: true });
    }
  }, [loginId, platform, params, setParams]);

  /* Fetch live platform credentials and receipt information */
  const {
    data: setupData,
    isLoading,
    isFetching,
  } = useGetAccountSetupQuery(
    { loginId, platform },
    { skip: !loginId }
  );

  const loading = ((isLoadingAccounts || isResolving) && !loginId) || isLoading || isFetching;

  const accountInfo = setupData?.account_information;
  const receipt = setupData?.receipt;

  const displayLogin = setupData?.login_id ?? accountInfo?.login ?? loginId;
  const displayPlatform = setupData?.platform ?? platform;

  // Platform credentials
  // No investor password: we don't offer one, so it isn't shown even when
  // the platform's record carries a value for it.
  const masterPassword = accountInfo?.master_password ?? null;
  const server = accountInfo?.server ?? NA;

  const webTerminalUrl =
    (typeof accountInfo?.web_terminal === 'string' && accountInfo.web_terminal) ||
    accountInfo?.web_terminal?.href ||
    null;

  const webTerminalLabel =
    (typeof accountInfo?.web_terminal === 'string' &&
      accountInfo.web_terminal.replace(/^https?:\/\//, '')) ||
    accountInfo?.web_terminal?.label ||
    '—';

  // Receipt details
  const isAvailable = receipt?.available !== false;
  const orderId = receipt?.order_id ?? null;
  const purchasedOn = formatPurchaseDate(receipt?.purchase_date);
  const receiptAmount = receipt?.amount ? formatReceiptAmount(receipt.amount) : null;
  const invoiceId = receipt?.order_id ?? receipt?.invoice_id ?? null;

  // Plan type and account size
  const planType = receipt?.plan_type ? titleCase(receipt.plan_type) : null;
  const sizeLabel = receipt?.account_size ? formatAccountSizeLabel(receipt.account_size) : null;
  const planTypeAndSize =
    planType && sizeLabel
      ? `${planType}, ${sizeLabel}`
      : planType || sizeLabel || null;

  const firstTradeSteps = buildFirstTradeSteps(
    setupData?.before_first_trade,
    receipt?.account_size
  );

  const invoiceHref = receipt?.invoice && receipt.invoice !== 'N/A' ? receipt.invoice : null;

  // Header meta summary
  const headerMeta = (() => {
    if (!displayLogin) return t('No account selected');
    const parts = [formatLoginId(displayLogin)];
    if (orderId && orderId !== NA) {
      parts.push(t('Order {{id}}', { id: orderId }));
    }
    if (purchasedOn && purchasedOn !== NA) {
      parts.push(t('purchased {{date}}', { date: purchasedOn }));
    }
    return parts.join(' · ');
  })();

  return (
    <div className="account-setup">
      <header className="page-head">
        <h1 className="page-head__title">{t('Account setup')}</h1>
        {loading ? (
          <Skeleton w="280px" h={14} />
        ) : (
          <p className="page-head__meta">{headerMeta}</p>
        )}
      </header>

      <InfoPanel
        title={t('Platform credentials')}
        foot={
          <>
            {t('Account Metrics keeps them for every account you hold.')}{' '}
            <Link className="ip-link" to="/account-metrics">
              {t('Open Account Metrics')}
            </Link>
          </>
        }
      >
        {loading ? (
          <div className="ip-fields">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="ip-field">
                <Skeleton w="60px" h={12} />
                <div className="ip-field__row" style={{ marginTop: 4 }}>
                  <Skeleton w="90px" h={16} />
                  <Skeleton w="38px" h={18} pill />
                </div>
              </div>
            ))}
          </div>
        ) : !displayLogin ? (
          <div className="ip-fields">
            <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
              {t('No trading account selected. Pick an account from the top bar to view credentials.')}
            </span>
          </div>
        ) : (
          <div className="ip-fields">
            <InfoField
              label={t('Login')}
              value={formatLoginId(displayLogin)}
              action={<CopyPill value={displayLogin} label={t('Login')} />}
            />
            <SecretField label={t('Master password')} secret={masterPassword} />
            <InfoField
              label={t('Server')}
              value={server}
              action={server && server !== NA ? <CopyPill value={server} label={t('Server')} /> : null}
            />
            {/* Only when the API names a web terminal (TI review N45) — it
                sends `web_terminal: null` for MT5, and the row used to sit
                there with "—" and an Open that went nowhere. */}
            {webTerminalUrl && (
            <InfoField
              label={t('Web terminal')}
              value={
                <span
                  className="ip-field__trunc"
                  title={typeof webTerminalLabel === 'string' ? webTerminalLabel : undefined}
                >
                  {webTerminalLabel}
                </span>
              }
              action={
                <a
                  className="ip-mini"
                  href={webTerminalUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t('Open')}
                </a>
              }
            />
            )}
          </div>
        )}
      </InfoPanel>

      <div className="page-two">
        <InfoPanel
          title={t('Get the platform')}
          foot={
            <Link className="ip-link" to="/downloads">
              {t('See all platforms')}
            </Link>
          }
        >
          <div className="ip-rows">
            {PLATFORMS.map((item) => (
              <InfoRow
                key={item.id}
                logo={item.logo}
                label={t(item.label)}
                note={t(item.meta)}
                href={item.href}
                to={item.to}
              />
            ))}
          </div>
        </InfoPanel>

        <InfoPanel
          title={t('Receipt')}
          foot={
            <>
              {t('Every receipt stays in')}{' '}
              <Link className="ip-link" to="/payment-history">
                {t('Payment History')}
              </Link>
              .
            </>
          }
        >
          {loading ? (
            <div className="as-receipt">
              <div className="as-receipt__id" style={{ gap: 6 }}>
                <Skeleton w="100px" h={24} />
                <Skeleton w="160px" h={12} />
                <Skeleton w="130px" h={12} />
              </div>
              <Skeleton w="100px" h={29} radius={5} />
            </div>
          ) : !displayLogin ? (
            <div className="as-receipt">
              <div className="as-receipt__id">
                <span className="as-receipt__amount" style={{ fontSize: '18px', color: 'var(--text-muted)' }}>
                  {t('No account selected')}
                </span>
                <span className="as-receipt__meta">{t('Select an account to view receipt details')}</span>
              </div>
            </div>
          ) : !isAvailable ? (
            <div className="as-receipt">
              <div className="as-receipt__id">
                <span className="as-receipt__amount" style={{ fontSize: '18px', color: 'var(--text-muted)' }}>
                  {t('Not available')}
                </span>
                <span className="as-receipt__meta">
                  {t(receipt?.reason_text || receipt?.reason || 'No receipt available for this account')}
                </span>
              </div>
            </div>
          ) : (
            <div className="as-receipt">
              <div className="as-receipt__id">
                <span className="as-receipt__amount">{receiptAmount || NA}</span>
                {invoiceId && (
                  <span className="as-receipt__meta">{t('Invoice {{id}}', { id: invoiceId })}</span>
                )}
                <span className="as-receipt__meta">
                  {receipt?.challenge_name || planTypeAndSize || NA}
                </span>
              </div>

              {invoiceHref ? (
                <a
                  className="as-cta"
                  href={invoiceHref}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t('Download PDF')}
                </a>
              ) : (
                <button
                  type="button"
                  className="as-cta is-disabled"
                  disabled
                  aria-disabled="true"
                  title={t('Invoice not available')}
                >
                  {t('Download PDF')}
                </button>
              )}
            </div>
          )}
        </InfoPanel>
      </div>

      <InfoPanel title={t('Before your first trade')}>
        {loading ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <Skeleton w="220px" h={14} />
            <Skeleton w="320px" h={12} />
            <Skeleton w="220px" h={14} />
            <Skeleton w="320px" h={12} />
          </div>
        ) : firstTradeSteps.length === 0 ? (
          <p className="as-step__body">{NA}</p>
        ) : (
          <ol className="as-steps">
            {firstTradeSteps.map((step, i) => (
              <li className="as-step" key={step.id}>
                <span className="as-step__n" aria-hidden="true">
                  {i + 1}
                </span>
                <div className="as-step__copy">
                  <span className="as-step__title">{step.title}</span>
                  {step.body && (
                    <p className="as-step__body">
                      {step.body}
                      {step.link && (
                        <>
                          {' '}
                          {/* A rule's own article lives in the help centre, off
                              this app — so it opens as an external link, while
                              the in-app fallback stays a router Link. */}
                          {step.link.href ? (
                            <a
                              className="ip-link"
                              href={step.link.href}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {step.link.label}
                            </a>
                          ) : (
                            <Link className="ip-link" to={step.link.to}>
                              {step.link.label}
                            </Link>
                          )}
                        </>
                      )}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </InfoPanel>

      <p className="page-note">
        {t(
          'This page follows the account selected in the top bar, the same way Account Metrics does. Switch account there and this page switches with it.',
        )}
      </p>
    </div>
  );
}
